import React, { useState, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import SEO from "../components/SEO";
import Breadcrumb from "../components/Breadcrumb";
import {
  ClipboardCheck,
  User,
  KeyRound,
  Globe,
  Target,
  Send,
  Loader2,
  CheckCircle,
  ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { INITIAL_FORM, canProceedFromStep } from "./onboarding/formConfig";
import { ContactBusinessStep } from "./onboarding/ContactBusinessStep";
import { MarketingGoalsStep } from "./onboarding/MarketingGoalsStep";
import { PlatformAccessStep } from "./onboarding/PlatformAccessStep";
import { WebsiteToolsStep } from "./onboarding/WebsiteToolsStep";
import { ReviewSubmitStep } from "./onboarding/ReviewSubmitStep";

const STEPS = [
  { id: 1, label: "Contact", icon: User },
  { id: 2, label: "Marketing", icon: Target },
  { id: 3, label: "Access", icon: KeyRound },
  { id: 4, label: "Tools", icon: Globe },
  { id: 5, label: "Submit", icon: Send },
];

const SuccessScreen = ({ onReset }) => (
  <div className="min-h-screen bg-[#0a0e1a] flex items-center justify-center px-4">
    <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="max-w-lg w-full text-center py-20">
      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#00B5E2] to-[#3056D3] flex items-center justify-center mx-auto mb-6">
        <CheckCircle className="w-10 h-10 text-white" />
      </div>
      <h2 className="text-3xl font-bold text-white mb-4" data-testid="success-heading">Questionnaire Submitted</h2>
      <p className="text-gray-400 text-lg mb-8 leading-relaxed">
        Thank you for completing the onboarding questionnaire. Our team will review your information and reach out within 24 hours to discuss your tailored digital marketing strategy.
      </p>
      <Button onClick={onReset} className="bg-gradient-to-r from-[#3056D3] to-[#00B5E2] hover:opacity-90 text-white px-8 py-3 rounded-full" data-testid="submit-another-btn">
        Submit Another Response
      </Button>
    </motion.div>
  </div>
);

const getStepStyle = (isActive, isComplete) => {
  if (isActive) return "bg-gradient-to-r from-[#3056D3] to-[#00B5E2] text-white";
  if (isComplete) return "bg-white/10 text-[#00B5E2] cursor-pointer hover:bg-white/15";
  return "bg-white/5 text-gray-500 cursor-default";
};

const StepIndicator = ({ currentStep, onStepClick }) => (
  <div className="flex items-center justify-center gap-1.5 sm:gap-2 mb-10 flex-wrap" data-testid="step-indicator">
    {STEPS.map((step, i) => {
      const Icon = step.icon;
      const isActive = currentStep === step.id;
      const isComplete = currentStep > step.id;
      return (
        <React.Fragment key={step.id}>
          <button
            onClick={() => { if (isComplete) onStepClick(step.id); }}
            className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all ${getStepStyle(isActive, isComplete)}`}
            data-testid={`step-${step.id}-btn`}
          >
            <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">{step.label}</span>
          </button>
          {i < STEPS.length - 1 && <div className={`w-4 sm:w-8 h-0.5 ${isComplete ? "bg-[#00B5E2]" : "bg-white/10"}`} />}
        </React.Fragment>
      );
    })}
  </div>
);

const ClientOnboardingPage = () => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const toggleChannel = useCallback((channel) => {
    setFormData((prev) => ({
      ...prev,
      current_channels: prev.current_channels.includes(channel)
        ? prev.current_channels.filter((c) => c !== channel)
        : [...prev.current_channels, channel],
    }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/onboarding`, formData);
      toast.success(response.data.message);
      setIsSubmitted(true);
      setFormData(INITIAL_FORM);
      setCurrentStep(1);
    } catch (error) {
      const msg = error.response?.data?.detail?.[0]?.msg || error.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please fill in all required fields.");
    }
    setIsSubmitting(false);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1: return <ContactBusinessStep formData={formData} onChange={handleChange} />;
      case 2: return <MarketingGoalsStep formData={formData} onChange={handleChange} toggleChannel={toggleChannel} />;
      case 3: return <PlatformAccessStep formData={formData} onChange={handleChange} />;
      case 4: return <WebsiteToolsStep formData={formData} onChange={handleChange} />;
      case 5: return <ReviewSubmitStep formData={formData} onChange={handleChange} />;
      default: return null;
    }
  };

  if (isSubmitted) {
    return (
      <>
        <SEO title="Client Onboarding - Start Your Growth Strategy" description="Complete the OptimaGrowth client onboarding questionnaire. Help us understand your business so we can build a tailored SEO, marketing automation, and growth strategy." keywords="client onboarding, marketing strategy questionnaire, SEO consultation form, business growth assessment" />
        <SuccessScreen onReset={() => setIsSubmitted(false)} />
      </>
    );
  }

  return (
    <>
      <SEO title="Client Onboarding - Start Your Growth Strategy" description="Complete the OptimaGrowth client onboarding questionnaire. Help us understand your business so we can build a tailored SEO, marketing automation, and growth strategy." keywords="client onboarding, marketing strategy questionnaire, SEO consultation form, business growth assessment" />

      <section className="relative bg-[#0a0e1a] pt-28 pb-12 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#3056D3] rounded-full blur-[120px]" />
          <div className="absolute bottom-10 right-10 w-72 h-72 bg-[#00B5E2] rounded-full blur-[120px]" />
        </div>
        <Breadcrumb items={[{ name: "Client Onboarding" }]} variant="dark" />
        <div className="relative max-w-3xl mx-auto text-center px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-5 py-2 mb-6">
              <ClipboardCheck className="w-4 h-4 text-[#00B5E2]" />
              <span className="text-sm text-gray-300">Client Onboarding</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4 leading-tight" data-testid="onboarding-heading">
              Let's Build Your<br />
              <span className="bg-gradient-to-r from-[#00B5E2] to-[#3056D3] bg-clip-text text-transparent">Digital Strategy</span>
            </h1>
            <p className="text-gray-400 text-base sm:text-lg max-w-2xl mx-auto">
              Help us understand your business and current digital presence so we can tailor a marketing strategy that delivers results. This takes about 5-8 minutes.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="bg-[#0a0e1a] pb-24 px-4">
        <div className="max-w-3xl mx-auto">
          <StepIndicator currentStep={currentStep} onStepClick={setCurrentStep} />

          <motion.div key={currentStep} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }} className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 sm:p-10">
            <form onSubmit={handleSubmit}>
              {renderStep()}

              <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/10">
                {currentStep > 1 ? (
                  <Button type="button" variant="ghost" onClick={() => setCurrentStep((s) => s - 1)} className="text-gray-400 hover:text-white hover:bg-white/5" data-testid="btn-back">Back</Button>
                ) : <div />}

                {currentStep < 5 ? (
                  <Button type="button" onClick={() => setCurrentStep((s) => s + 1)} disabled={!canProceedFromStep(currentStep, formData)} className="bg-gradient-to-r from-[#3056D3] to-[#00B5E2] hover:opacity-90 text-white rounded-full px-6 disabled:opacity-40" data-testid="btn-next">
                    Continue <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                ) : (
                  <Button type="submit" disabled={isSubmitting} className="bg-gradient-to-r from-[#D6006E] to-[#3056D3] hover:opacity-90 text-white rounded-full px-8 disabled:opacity-60" data-testid="btn-submit">
                    {isSubmitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>) : (<>Submit Questionnaire <Send className="w-4 h-4 ml-1" /></>)}
                  </Button>
                )}
              </div>
            </form>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default ClientOnboardingPage;
